import { array, bool, number } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import Menu, { MenuItem, Placements } from '@seaweb/coral/components/Menu';
import Tabs, {
  Tab,
  TabList,
  TabUnderline,
} from '@seaweb/coral/components/Tabs';
import styled from 'styled-components';
import Link from 'next/link';

const hiddenStyle = `
  position: fixed;
  top: 8px;
  left: 0;
  width: 100%;
`;

const stickyTopStyle = `
  position: fixed;
  top: 64px;
  left: 0;
  width: 100%;
`;

export const calculateCategoryTabStyle = (props) => {
  const { isScrollDown, beyondThreshold, notHiddenWhenScrollDown } = props;

  if (notHiddenWhenScrollDown || !isScrollDown) {
    return beyondThreshold ? stickyTopStyle : '';
  }

  return beyondThreshold ? hiddenStyle : '';
};

const ContentWrapper = styled.div`
  background-color: #f9fbfe;
  z-index: 4;
  ${calculateCategoryTabStyle}
  ${(props) =>
    props.beyondThreshold
      ? `
        box-shadow: rgb(102 138 184 / 10%) 0px 16px 30px 0px;
        transition: all 0.15s ease;`
      : ''}

  a {
    color: inherit;
  }
`;

const TabListWrapper = styled.div`
  display: flex;
  margin: 0 auto;
  max-width: 1160px;

  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }

  [role='tablist'] {
    height: 56px;
    box-shadow: unset;
  }

  @media screen and (max-width: 1025px) {
    width: 100%;
    overflow: auto;
  }
`;

const StyledTabUnderline = styled(TabUnderline)`
  height: 2px;
  background-color: unset;
  border-radius: 2px;
  margin-top: 2px;

  div {
    width: calc(100% - 30px);
    height: 2px;
    transition: all 0.2s ease;
    background-color: #1b92f5;
    margin: auto;
  }
`;

const MenuInner = styled.div`
  a {
    width: 100%;
    height: 56px;
    line-height: 56px;
    display: block;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  a {
    width: 100%;
    height: 39px;
    line-height: 39px;
    display: block;
  }
`;

const CategoryTabList = ({
  currentCategoryId,
  categories,
  isScrollDown,
  beyondThreshold,
  notHiddenWhenScrollDown,
}) => {
  const [hoverCategory, setHoverCategory] = useState(null);
  const [enableLine, setEnableLine] = useState(false);
  const categoryIds = useMemo(() => {
    return categories.map((category) => category.id);
  }, [categories]);

  // Work around because Coral calculates wrongly the position of underline
  useEffect(() => {
    setTimeout(() => {
      setEnableLine(true);
    }, 300);
  }, []);

  return (
    <div style={{ height: '56px' }}>
      <ContentWrapper
        isScrollDown={isScrollDown}
        beyondThreshold={beyondThreshold}
        notHiddenWhenScrollDown={notHiddenWhenScrollDown}
      >
        <TabListWrapper>
          {typeof window !== `undefined` && (
            <Tabs index={categoryIds.indexOf(currentCategoryId)}>
              <TabList
                underline={
                  enableLine ? (
                    <StyledTabUnderline>
                      <div />
                    </StyledTabUnderline>
                  ) : (
                    <div />
                  )
                }
              >
                {categories.map((category) => (
                  <Tab
                    key={category.id}
                    style={{ minHeight: 48 }}
                    onMouseEnter={() => setHoverCategory(category.id)}
                    onMouseLeave={() => setHoverCategory(null)}
                  >
                    <Menu
                      placement={Placements.BottomStart}
                      offset={[0, -1]}
                      button={
                        <MenuInner>
                          <Link href={`/categories/${category.id}`}>
                            {category.name}
                          </Link>
                        </MenuInner>
                      }
                      active={category.id === hoverCategory}
                      onActiveChange={(b) =>
                        setHoverCategory({ active: b ? category.id : null })
                      }
                      style={{ marginLeft: '-16px', marginTop: '5px' }}
                    >
                      {category.subCategories.map((subCategory) => (
                        <StyledMenuItem
                          key={subCategory.id}
                          index={subCategory.id}
                        >
                          <Link
                            href={`/categories/${category.id}#${subCategory.id}`}
                          >
                            {subCategory.name}
                          </Link>
                        </StyledMenuItem>
                      ))}
                    </Menu>
                  </Tab>
                ))}
              </TabList>
            </Tabs>
          )}
        </TabListWrapper>
      </ContentWrapper>
    </div>
  );
};

CategoryTabList.propTypes = {
  categories: array,
  currentCategoryId: number,
  isScrollDown: bool,
  beyondThreshold: bool,
  notHiddenWhenScrollDown: bool,
};

export default CategoryTabList;
